import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import api from "@/lib/api";
import BalbisLogo from "@/components/BalbisLogo";
import {
  Building2, ClipboardList, Clock, Users, FileText, Settings,
  LogOut, LayoutDashboard, Bell, MessageSquare, Menu, X, TrendingUp, CreditCard,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/clients", icon: Building2, label: "Clients" },
  { to: "/requests", icon: ClipboardList, label: "Tasks" },
  { to: "/messages", icon: MessageSquare, label: "Messages" },
  { to: "/analytics", icon: TrendingUp, label: "Analytics" },
  { to: "/time-tracking", icon: Clock, label: "Time" },
  { to: "/team", icon: Users, label: "Team" },
  { to: "/billing", icon: CreditCard, label: "Billing" },
  { to: "/invoices", icon: FileText, label: "Invoices" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState({ count: 0, threads: [] });
  const [showBell, setShowBell] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const bellRef = useRef(null);

  const fetchUnread = () =>
    api.get("/requests/notifications/unread").then((r) => setUnread(r.data)).catch(() => {});

  useEffect(() => { fetchUnread(); }, []);

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.sender_type === "client") fetchUnread();
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    const handler = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile nav on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Auto-clear notification bell when Messages page is visited
  useEffect(() => {
    if (location.pathname === "/messages") {
      setUnread({ count: 0, threads: [] });
    }
  }, [location.pathname]);

  const isActive = (to) => to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const SidebarContent = () => (
    <>
      <div className="px-5 pt-6 pb-5 border-b" style={{ borderColor: "#F5F3EF" }}>
        <BalbisLogo style={{ height: "22px", width: "auto", color: "#292524" }} />
        <p className="text-xs mt-1.5 tracking-widest uppercase font-medium" style={{ color: "#A8A29E" }}>
          Service Hub
        </p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label }) => {
          const active = isActive(to);
          return (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors"
              style={{
                background: active ? "#F5F3EF" : "transparent",
                color: active ? "#292524" : "#78716C",
                fontWeight: active ? 500 : 400,
              }}
            >
              <Icon size={15} strokeWidth={1.5} />
              {label}
            </Link>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t" style={{ borderColor: "#F5F3EF" }}>
        <Link
          to="/settings"
          data-testid="nav-settings"
          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors hover:bg-stone-100"
          style={{ color: "#78716C" }}
        >
          <Settings size={15} strokeWidth={1.5} />
          Settings
        </Link>
        {user && (
          <div
            className="mt-3 px-3 py-2 flex items-center gap-2.5 rounded-lg cursor-pointer hover:bg-stone-50 transition-colors"
            onClick={() => navigate("/profile")}
            data-testid="user-profile-link"
          >
            <div className="w-7 h-7 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-white text-xs font-semibold"
              style={{ background: user.avatar_color || "#D97706" }}>
              {user.avatar_image
                ? <img src={user.avatar_image} alt="" className="w-full h-full object-cover" />
                : user.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate" style={{ color: "#292524" }}>{user.name}</p>
              <p className="text-xs truncate" style={{ color: "#A8A29E" }}>View profile</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); logout(); navigate("/admin"); }}
              data-testid="logout-button"
              className="p-1 rounded hover:bg-red-50 flex-shrink-0"
              style={{ color: "#A8A29E" }}
            >
              <LogOut size={13} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#FBF9F6" }}>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-56 flex-col border-r flex-shrink-0" style={{ background: "#FFFFFF", borderColor: "#E7E5E4" }}>
        <SidebarContent />
      </aside>

      {/* Mobile drawer backdrop */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed top-0 left-0 h-full z-50 w-64 flex flex-col border-r md:hidden transition-transform duration-200 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "#FFFFFF", borderColor: "#E7E5E4" }}
      >
        <button
          onClick={() => setMobileNavOpen(false)}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-stone-100"
          style={{ color: "#78716C" }}
        >
          <X size={16} />
        </button>
        <SidebarContent />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-12 flex items-center justify-between px-4 border-b flex-shrink-0"
          style={{ background: "#FFFFFF", borderColor: "#E7E5E4" }}>
          {/* Hamburger - mobile only */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-stone-100"
            style={{ color: "#78716C" }}
            onClick={() => setMobileNavOpen(true)}
            data-testid="mobile-nav-toggle"
          >
            <Menu size={18} strokeWidth={1.5} />
          </button>
          <div className="hidden md:block" />

          {/* Notification Bell */}
          <div className="relative" ref={bellRef}>
            <button
              data-testid="notification-bell"
              onClick={() => setShowBell((p) => !p)}
              className="relative p-2 rounded-lg hover:bg-stone-100 transition-colors"
              style={{ color: "#78716C" }}
            >
              <Bell size={16} strokeWidth={1.5} />
              {unread.count > 0 && (
                <span
                  data-testid="notification-badge"
                  className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center text-xs font-bold"
                  style={{ background: "#DC2626", fontSize: "10px" }}
                >
                  {unread.count > 9 ? "9+" : unread.count}
                </span>
              )}
            </button>
            {showBell && (
              <div
                className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl border shadow-lg z-50 overflow-hidden"
                style={{ borderColor: "#E7E5E4" }}
                data-testid="notification-dropdown"
              >
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E7E5E4" }}>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Unread Messages</p>
                  {unread.count > 0 && (
                    <button
                      data-testid="mark-all-read-button"
                      onClick={async () => { await api.post("/requests/notifications/mark-all-read"); setUnread({ count: 0, threads: [] }); }}
                      className="text-xs font-medium" style={{ color: "#D97706" }}
                    >Mark all read</button>
                  )}
                </div>
                {(unread.threads || []).length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm" style={{ color: "#A8A29E" }}>All caught up!</div>
                ) : (
                  <div className="divide-y" style={{ borderColor: "#F5F3EF" }}>
                    {(unread.threads || []).map((t) => (
                      <button
                        key={t.id}
                        data-testid={`notif-item-${t.id}`}
                        onClick={() => {
                          navigate(t.client_id ? `/clients/${t.client_id}?tab=messages` : `/messages`);
                          setShowBell(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-stone-50 transition-colors"
                      >
                        <p className="text-sm font-medium truncate" style={{ color: "#292524" }}>{t.title}</p>
                        <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>{t.client_name} · new message</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
